import { validatorClient } from "../../../companies/clients";
import { validatorRecipient } from "../../../companies/clients/recipients";
import { validatorBoxDelivery } from "../../../companies/logistics/box_deliveries";
import { validatorBoxType } from "../../../companies/logistics/box_types";
import { validatorConsolidationCalendar } from "../../../companies/logistics/calendars";
import { validatorCarrier } from "../../../companies/logistics/carriers";
import { validatorLogisticsConfiguration } from "../../../companies/logistics/configuration";
import { validatorContainer } from "../../../companies/logistics/containers";
import { validatorLogisticsOrder } from "../../../companies/logistics/logistics_orders";
import {
  paymentStatusRule,
  shipmentFilterRule,
} from "../../../companies/logistics/logistics_orders/filter_rules";
import { validatorOrderPayment } from "../../../companies/logistics/order_payments";
import { verificationStatusRule } from "../../../companies/logistics/order_payments/filter_rules";

import { validatorPackage } from "../../../companies/logistics/packages";
import { validatorLogisticsPaymentMethod } from "../../../companies/logistics/payment_methods";
import { validatorShipment } from "../../../companies/logistics/shipments";
import { validatorShippingRoute } from "../../../companies/logistics/shipping_routes";
import { validatorUsers } from "../../../system/users";
import { DomainGroup, TableScope } from "../types";

// ============================================================================
// LOGISTICS DOMAIN - I18n Status Values
// ============================================================================

/** Estados de órdenes de logística (para i18n) */
export const LOGISTICS_ORDER_STATUS = [
  "draft",
  "pending",
  "processing",
  "ready_to_ship",
  "shipped",
  "in_transit",
  "delivered",
  "cancelled",
] as const;

/** Estados de pago de órdenes (para i18n) */
export const LOGISTICS_PAYMENT_STATUS = [
  "pending",
  "partial",
  "paid",
  "refunded",
  "cancelled",
] as const;

/** Estados de paquetes (para i18n) */
export const PACKAGE_STATUS = [
  "registered",
  "in_warehouse",
  "in_consolidation",
  "shipped",
  "in_transit",
  "in_customs",
  "delivered",
  "returned",
] as const;

/** Estados de contenedores/shipments (para i18n) */
export const SHIPMENT_STATUS = [
  "open",
  "consolidating",
  "closed",
  "shipped",
  "in_transit",
  "arrived",
  "delivered",
] as const;

/** Estados de entrega de cajas (para i18n) */
export const BOX_DELIVERY_STATUS = [
  "pending",
  "delivered",
  "returned",
  "cancelled",
] as const;

// ============================================================================
// LOGISTICS DOMAIN CONFIGURATION
// ============================================================================

export const logisticsDomain: DomainGroup = {
  id: "logistics",
  name: "Logística",
  description: "Gestión de paquetes y envíos internacionales",
  icon: "Package",
  order: 2,

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "logistics",
    keys: {
      title: "logistics.title",
      description: "logistics.description",
    },
  },

  modules: [
    {
      // ── Metadata del módulo ──────────────────────────────
      id: "logistics_orders",
      name: "Órdenes de Envío",
      description: "Órdenes que agrupan múltiples paquetes de un remitente",
      icon: "ClipboardList",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/logistics/orders"],
      navigation: {
        showInNav: true,
        contexts: ["main", "mobile"],
        navOrder: { main: 0, mobile: 1 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "logistics.orders",
        keys: {
          title: "logistics.orders.title",
          description: "logistics.orders.description",
          longDescription: "logistics.orders.longDescription",
          searchPlaceholder: "logistics.orders.searchPlaceholder",
          emptyState: "logistics.orders.emptyState",
          actions: {
            create: "logistics.orders.actions.create",
            edit: "logistics.orders.actions.edit",
            delete: "logistics.orders.actions.delete",
            export: "logistics.orders.actions.export",
            view: "logistics.orders.actions.view",
          },
        },
        translatableFields: ["notes"],
        translatableEnums: [
          { field: "status", values: LOGISTICS_ORDER_STATUS },
          { field: "paymentStatus", values: LOGISTICS_PAYMENT_STATUS },
        ],
      },

      // ── Tablas que este módulo POSEE (write) ─────────────
      tables: [
        {
          tableName: "logistics_orders",
          schema: validatorLogisticsOrder,
          scope: "company" as TableScope,
          relations: [
            {
              key: "sender",
              sourceField: "senderId",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "code", "email"],
            },
            {
              key: "shipment",
              sourceField: "shipmentId",
              schema: validatorShipment,
              tableName: "shipments",
              searchableFields: ["name"],
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["orderNumber"],
          hasStatusIndex: true,
          customRules: [paymentStatusRule, shipmentFilterRule],
          searchIndex: {
            name: "search_logistics_orders",
            field: "orderNumber",
          },
        },
      ],

      // ── Tablas que este módulo solo LEE (read-only) ──────
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "packages" },
        { tableName: "shipments" },
      ],
    },
    {
      id: "packages",
      name: "Paquetes",
      description: "Registro y seguimiento de paquetes",
      icon: "PackageOpen",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/logistics/packages"],
      navigation: {
        showInNav: false,
        contexts: ["main", "mobile"],
        navOrder: { main: 2, mobile: 2 },
        status: "inactive",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "logistics.packages",
        keys: {
          title: "logistics.packages.title",
          description: "logistics.packages.description",
          longDescription: "logistics.packages.longDescription",
          searchPlaceholder: "logistics.packages.searchPlaceholder",
          emptyState: "logistics.packages.emptyState",
          actions: {
            create: "logistics.packages.actions.create",
            edit: "logistics.packages.actions.edit",
            delete: "logistics.packages.actions.delete",
            view: "logistics.packages.actions.view",
          },
        },
        translatableFields: ["merchandiseDescription", "notes"],
        translatableEnums: [{ field: "status", values: PACKAGE_STATUS }],
      },

      tables: [
        {
          tableName: "packages",
          schema: validatorPackage,
          scope: "company" as TableScope,
          relations: [
            {
              key: "sender",
              sourceField: "senderId",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["name", "code", "email"],
            },
            {
              key: "recipient",
              sourceField: "recipientId",
              schema: validatorRecipient,
              tableName: "recipients",
            },
            {
              key: "boxType",
              sourceField: "boxTypeId",
              schema: validatorBoxType,
              tableName: "box_types",
            },
            {
              key: "carrier",
              sourceField: "carrierId",
              schema: validatorCarrier,
              tableName: "carriers",
            },
            {
              key: "shipment",
              sourceField: "shipmentId",
              schema: validatorShipment,
              tableName: "shipments",
            },
            {
              key: "logisticsOrder",
              sourceField: "logisticsOrderId",
              schema: validatorLogisticsOrder,
              tableName: "logistics_orders",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: [
            "orderNumber",
            "trackingNumber",
            "merchandiseDescription",
          ],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "box_types" },
        { tableName: "carriers" },
        { tableName: "shipments" },
        { tableName: "logistics_orders" },
        { tableName: "branches" },
      ],
    },
    {
      id: "shipments",
      name: "Contenedores",
      description: "Contenedores y despachos maestros",
      icon: "Layers",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/logistics/containers"],
      navigation: {
        showInNav: true,
        contexts: ["main", "mobile"],
        navOrder: { main: 3, mobile: 3 },
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "logistics.shipments",
        keys: {
          title: "logistics.shipments.title",
          description: "logistics.shipments.description",
          longDescription: "logistics.shipments.longDescription",
          searchPlaceholder: "logistics.shipments.searchPlaceholder",
          emptyState: "logistics.shipments.emptyState",
          actions: {
            create: "logistics.shipments.actions.create",
            edit: "logistics.shipments.actions.edit",
            delete: "logistics.shipments.actions.delete",
            view: "logistics.shipments.actions.view",
          },
        },
        translatableEnums: [{ field: "status", values: SHIPMENT_STATUS }],
      },

      tables: [
        {
          tableName: "shipments",
          schema: validatorShipment,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "container",
              sourceField: "containerId",
              schema: validatorContainer,
              tableName: "containers",
            },
          ],
          searchFields: ["name"],
          hasStatusIndex: true,
          searchIndex: {
            name: "search_shipments",
            field: "name",
          },
        },
      ],
      readOnlyTables: [
        { tableName: "packages" },
        { tableName: "logistics_orders" },
        { tableName: "branches" },
        { tableName: "shipping_routes" },
      ],
    },
    {
      id: "logistics_payment_methods",
      name: "Métodos de Pago",
      description: "Configuración de métodos de pago para paquetes",
      icon: "CreditCard",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/payment-methods"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 10,
        status: "active",
      },
      tables: [
        {
          tableName: "logistics_payment_methods",
          schema: validatorLogisticsPaymentMethod,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "description", "bankName", "accountHolder"],
          hasStatusIndex: false,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "box_deliveries",
      name: "Entrega de Cajas",
      description:
        "Gestión de entregas de cajas vacías con depósito anticipado",
      icon: "Package",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/logistics/box-deliveries"],
      navigation: {
        showInNav: true,
        contexts: ["main", "mobile"],
        navOrder: 1,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "logistics.box_deliveries",
        keys: {
          title: "logistics.box_deliveries.title",
          description: "logistics.box_deliveries.description",
          searchPlaceholder: "logistics.box_deliveries.searchPlaceholder",
          emptyState: "logistics.box_deliveries.emptyState",
          actions: {
            create: "logistics.box_deliveries.actions.create",
            edit: "logistics.box_deliveries.actions.edit",
            delete: "logistics.box_deliveries.actions.delete",
            view: "logistics.box_deliveries.actions.view",
          },
        },
        translatableEnums: [{ field: "status", values: BOX_DELIVERY_STATUS }],
      },

      tables: [
        {
          tableName: "box_deliveries",
          schema: validatorBoxDelivery,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "client",
              sourceField: "clientId",
              schema: validatorClient,
              tableName: "clients",
              searchableFields: ["code", "name", "email"],
            },
            {
              key: "boxType",
              sourceField: "boxTypeId",
              schema: validatorBoxType,
              tableName: "box_types",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["qrCode"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "box_types" },
        { tableName: "logistics_configurations" },
      ],
    },
    {
      id: "box_types",
      name: "Tipos de Caja",
      description: "Catálogo de cajas y precios base",
      icon: "Boxes",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/box-types"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 5,
        status: "active",
      },
      tables: [
        {
          tableName: "box_types",
          schema: validatorBoxType,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "carriers",
      name: "Carriers",
      description: "Transportistas y operadores logísticos",
      icon: "Truck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/carriers"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 6,
        status: "active",
      },
      tables: [
        {
          tableName: "carriers",
          schema: validatorCarrier,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "shipping_routes",
      name: "Rutas de Envío",
      description: "Rutas configuradas por tenant entre sucursales",
      icon: "Route",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/shipping-routes"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 7,
        status: "active",
      },
      tables: [
        {
          tableName: "shipping_routes",
          schema: validatorShippingRoute,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
    {
      id: "containers",
      name: "Contenedores",
      description: "Catálogo de contenedores y capacidades",
      icon: "Container",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/containers"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 8,
        status: "active",
      },
      tables: [
        {
          tableName: "containers",
          schema: validatorContainer,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "consolidation_calendars",
      name: "Calendarios",
      description: "Calendarios periódicos de envío",
      icon: "Calendar",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/calendars"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 9,
        status: "active",
      },
      tables: [
        {
          tableName: "consolidation_calendars",
          schema: validatorConsolidationCalendar,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [
        { tableName: "containers" },
        { tableName: "shipping_routes" },
        { tableName: "carriers" },
      ],
    },
    {
      id: "logistics_configurations",
      name: "Logística",
      description: "Configuración general del módulo logístico",
      icon: "Settings2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/logistics"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 11,
        status: "active",
      },
      tables: [
        {
          tableName: "logistics_configurations",
          schema: validatorLogisticsConfiguration,
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [
        { tableName: "consolidation_calendars" },
        { tableName: "branches" },
      ],
    },
    {
      id: "order_payments",
      name: "Pagos de Órdenes",
      description: "Registro y verificación de pagos a nivel de orden",
      icon: "Receipt",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/logistics/order_payments"],
      navigation: {
        showInNav: true,
        contexts: ["main", "mobile"],
        navOrder: 12,
        status: "active",
      },
      tables: [
        {
          tableName: "order_payments",
          schema: validatorOrderPayment,
          scope: "company" as TableScope,
          relations: [
            {
              key: "logisticsOrder",
              sourceField: "logisticsOrderId",
              schema: validatorLogisticsOrder,
              tableName: "logistics_orders",
            },
            {
              key: "paymentMethod",
              sourceField: "paymentMethodId",
              schema: validatorLogisticsPaymentMethod,
              tableName: "logistics_payment_methods",
            },
            {
              key: "verifiedByUser",
              sourceField: "verifiedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "rejectedByUser",
              sourceField: "rejectedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["reference"],
          customRules: [verificationStatusRule],
        },
      ],
      readOnlyTables: [
        { tableName: "logistics_orders" },
        { tableName: "logistics_payment_methods" },
      ],
    },
  ],
};
